/**
 * Build React Flow nodes/edges for a criterion-collapsed protocol DAG.
 */

import type { Edge, Node } from "@xyflow/react";
import type {
  ProtocolAtom,
  ProtocolConnection,
  ProtocolCriterion,
  ProtocolGate,
} from "../../api/clinicalTrialsApiTypes";
import {
  isExecutionConnection,
  type ProtocolNodeKind,
} from "./protocolDagConnectionRules";

export type ProtocolDagNodeData = {
  kind: ProtocolNodeKind;
  label: string;
  subtitle: string;
  hardStop: boolean;
  needsReview: boolean;
  criterionId: string;
  atomCount?: number;
  expanded?: boolean;
  entityId: string;
};

export type ProtocolDagEdgeData = {
  connectionId: string;
  connectionType: string;
  execution: boolean;
  proxy: boolean;
};

export type BuiltProtocolGraph = {
  nodes: Node<ProtocolDagNodeData>[];
  edges: Edge<ProtocolDagEdgeData>[];
};

/**
 * Resolve the owning criterion id for a protocol node id.
 *
 * @param nodeId - Criterion, atom, or gate id
 * @param atoms - Draft atoms
 * @param gates - Draft gates
 * @returns Criterion id when known
 */
export function criterionIdForNode(
  nodeId: string,
  atoms: ProtocolAtom[],
  gates: ProtocolGate[],
): string | null {
  const atom = atoms.find((item) => item.atom_id === nodeId);
  if (atom) return atom.criterion_id;
  const gate = gates.find((item) => item.gate_id === nodeId);
  if (gate) return gate.criterion_id;
  return null;
}

/**
 * Map a rendered graph endpoint for a connection endpoint.
 *
 * Collapsed criteria act as proxies for their hidden child nodes.
 *
 * @param nodeId - Canonical entity id
 * @param nodeType - Entity kind
 * @param expandedCriterionId - Currently expanded criterion, if any
 * @param atoms - Draft atoms
 * @param gates - Draft gates
 * @returns Visible React Flow node id, or null when omitted
 */
export function visibleEndpointId(
  nodeId: string,
  nodeType: string,
  expandedCriterionId: string | null,
  atoms: ProtocolAtom[],
  gates: ProtocolGate[],
): string | null {
  if (nodeType === "criterion") {
    return nodeId;
  }
  const owner = criterionIdForNode(nodeId, atoms, gates);
  if (!owner) return null;
  if (owner === expandedCriterionId) {
    return nodeId;
  }
  return owner;
}

/**
 * Build graph elements for the interactive protocol DAG.
 *
 * @param criteria - Draft criteria
 * @param atoms - Draft atoms
 * @param gates - Draft gates
 * @param connections - Draft connections
 * @param expandedCriterionId - Criterion currently expanded in the canvas
 * @returns React Flow nodes and edges
 */
export function buildProtocolGraph(
  criteria: ProtocolCriterion[],
  atoms: ProtocolAtom[],
  gates: ProtocolGate[],
  connections: ProtocolConnection[],
  expandedCriterionId: string | null,
): BuiltProtocolGraph {
  const sorted = [...criteria].sort((a, b) => a.eval_order - b.eval_order);
  const nodes: Node<ProtocolDagNodeData>[] = sorted.map((criterion) => {
    const childAtoms = atoms.filter(
      (atom) => atom.criterion_id === criterion.criterion_id,
    );
    const expanded = expandedCriterionId === criterion.criterion_id;
    return {
      id: criterion.criterion_id,
      type: "protocolNode",
      position: { x: 0, y: 0 },
      data: {
        kind: "criterion",
        label: criterion.criterion_no,
        subtitle: `${criterion.criterion_type} · ${childAtoms.length} atoms`,
        hardStop: criterion.has_hard_stop,
        needsReview: childAtoms.some((atom) => atom.needs_review),
        criterionId: criterion.criterion_id,
        atomCount: childAtoms.length,
        expanded,
        entityId: criterion.criterion_id,
      },
    };
  });

  if (expandedCriterionId) {
    for (const gate of gates.filter(
      (item) => item.criterion_id === expandedCriterionId,
    )) {
      nodes.push({
        id: gate.gate_id,
        type: "protocolNode",
        position: { x: 0, y: 0 },
        data: {
          kind: "gate",
          label: gate.label,
          subtitle: `${gate.operator}${gate.is_hard_stop ? " · hard-stop" : ""}`,
          hardStop: gate.is_hard_stop,
          needsReview: false,
          criterionId: gate.criterion_id,
          entityId: gate.gate_id,
        },
      });
    }
    for (const atom of atoms.filter(
      (item) => item.criterion_id === expandedCriterionId,
    )) {
      nodes.push({
        id: atom.atom_id,
        type: "protocolNode",
        position: { x: 0, y: 0 },
        data: {
          kind: "atom",
          label: atom.atom_id,
          subtitle: atom.atom_text,
          hardStop: atom.is_hard_stop,
          needsReview: atom.needs_review,
          criterionId: atom.criterion_id,
          entityId: atom.atom_id,
        },
      });
    }
  }

  const visibleIds = new Set(nodes.map((node) => node.id));
  const edges: Edge<ProtocolDagEdgeData>[] = [];
  for (const connection of connections) {
    const source = visibleEndpointId(
      connection.from_node_id,
      connection.from_node_type,
      expandedCriterionId,
      atoms,
      gates,
    );
    const target = visibleEndpointId(
      connection.to_node_id,
      connection.to_node_type,
      expandedCriterionId,
      atoms,
      gates,
    );
    if (!source || !target || source === target) continue;
    if (!visibleIds.has(source) || !visibleIds.has(target)) continue;
    const proxy =
      source !== connection.from_node_id || target !== connection.to_node_id;
    edges.push({
      id: connection.connection_id,
      source,
      target,
      type: "smoothstep",
      animated: isExecutionConnection(connection.connection_type),
      label: connection.connection_type,
      data: {
        connectionId: connection.connection_id,
        connectionType: connection.connection_type,
        execution: isExecutionConnection(connection.connection_type),
        proxy,
      },
      style: {
        stroke: isExecutionConnection(connection.connection_type)
          ? "#0f172a"
          : "#64748b",
        strokeWidth: proxy ? 1.5 : 2,
        strokeDasharray: proxy ? "6 4" : undefined,
      },
      labelStyle: { fontSize: 10, fill: "#475569" },
    });
  }

  return { nodes, edges };
}
